import { useEffect, useState, type ReactNode, type FormEvent } from 'react';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DataGrid } from '../../components/DataGrid.js';
import {
  listFeatures,
  setFeature,
  FEATURE_SCOPES,
  FEATURE_SCOPE_LABELS,
  type FeatureItem,
  type SetFeatureInput,
} from '../../api/featuresApi.js';
import { DateInput, validateDateSubmission } from '../../../../shared/components/DateInput.js';

function toIso(local: string): string | undefined {
  if (!local) return undefined;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  // strip seconds/timezone for datetime-local
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}

interface FormState {
  featureCode: string;
  description: string;
  scope: string;
  enabled: boolean;
  effectiveFrom: string;
  effectiveTo: string;
}

export function FeaturesPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<readonly FeatureItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const reload = () =>
    listFeatures(token)
      .then((r) => setRows(r))
      .catch((e) => setError((e as Error).message));

  useEffect(() => {
    let alive = true;
    listFeatures(token)
      .then((r) => {
        if (alive) setRows(r);
      })
      .catch((e) => {
        if (alive) setError((e as Error).message);
      });
    return () => {
      alive = false;
    };
  }, [token]);

  const startEdit = (f: FeatureItem) => {
    setStatus(null);
    setError(null);
    setForm({
      featureCode: f.featureCode,
      description: f.description,
      scope: f.scope ?? 'COMPANY',
      enabled: f.enabled,
      effectiveFrom: toLocalInput(f.effectiveFrom),
      effectiveTo: toLocalInput(f.effectiveTo),
    });
  };

  const onSubmit = async (ev: FormEvent) => {
    ev.preventDefault();
    if (!form) return;
    setError(null);
    setStatus(null);
    setSaving(true);
    try {
      const from = toIso(form.effectiveFrom);
      const to = toIso(form.effectiveTo);
      const body: SetFeatureInput = {
        featureCode: form.featureCode,
        scope: form.scope as SetFeatureInput['scope'],
        enabled: form.enabled,
        ...(from ? { effectiveFrom: from } : {}),
        ...(to ? { effectiveTo: to } : {}),
      };
      await setFeature(body, token);
      setStatus(t('features.saved', { code: form.featureCode }));
      setForm(null);
      await reload();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (error && !rows) return <p role="alert">{error}</p>;
  if (!rows) return <p>{t('features.loading')}</p>;

  return (
    <main aria-label="features-list">
      <h1>{t('features.title')}</h1>
      {error ? <p role="alert">{error}</p> : null}
      {status ? <p role="status">{status}</p> : null}

      <DataGrid<FeatureItem>
        caption="features-table"
        rows={rows}
        getRowId={(f) => f.featureCode}
        rowTestId={(f) => `row-${f.featureCode}`}
        searchPlaceholder={t('features.searchPlaceholder')}
        exportFileName="features.csv"
        columns={[
          { key: 'featureCode', header: t('features.col.code'), value: (f) => f.featureCode },
          { key: 'description', header: t('features.col.description'), value: (f) => f.description },
          { key: 'default', header: t('features.col.default'), value: (f) => (f.defaultEnabled ? 'ON' : 'OFF') },
          { key: 'effective', header: t('features.col.effective'), value: (f) => (f.enabled ? 'ON' : 'OFF') },
          { key: 'scope', header: t('features.col.scope'), value: (f) => f.scope ?? '—' },
          { key: 'override', header: t('features.col.override'), value: (f) => (f.hasOverride ? t('features.yes') : t('features.no')) },
        ]}
        actions={(f) => (
          <button
            type="button"
            onClick={() => startEdit(f)}
            data-testid={`edit-${f.featureCode}`}
          >
            {t('features.configure')}
          </button>
        )}
        actionsHeader={t('features.col.action')}
      />

      {form ? (
        <form onSubmit={onSubmit} aria-label="feature-form" noValidate onSubmitCapture={validateDateSubmission}>
          <h2>{t('features.configureTitle', { code: form.featureCode })}</h2>
          <p data-testid="feature-description">{form.description}</p>

          <div className="feature-field">
            <label htmlFor="ff-scope">{t('features.field.scope')}</label>
            <select
              id="ff-scope"
              value={form.scope}
              onChange={(e) =>
                setForm((s) => (s ? { ...s, scope: e.target.value } : s))
              }
              data-testid="field-scope"
            >
              {FEATURE_SCOPES.map((s) => (
                <option key={s} value={s}>
                  {FEATURE_SCOPE_LABELS[s] ?? s}
                </option>
              ))}
            </select>
          </div>

          <div className="feature-field">
            <label htmlFor="ff-enabled">{t('features.field.enabled')}</label>
            <input
              id="ff-enabled"
              type="checkbox"
              checked={form.enabled}
              onChange={(e) =>
                setForm((s) => (s ? { ...s, enabled: e.target.checked } : s))
              }
              data-testid="field-enabled"
            />
          </div>

          <div className="feature-field">
            <label htmlFor="ff-from">{t('features.field.effectiveFrom')}</label>
            <DateInput
              id="ff-from"
              type="datetime-local"
              value={form.effectiveFrom}
              onValueChange={(nextValue) =>
                setForm((s) =>
                  s ? { ...s, effectiveFrom: nextValue } : s,
                )
              }
              data-testid="field-effectiveFrom"
            />
          </div>

          <div className="feature-field">
            <label htmlFor="ff-to">{t('features.field.effectiveTo')}</label>
            <DateInput
              id="ff-to"
              type="datetime-local"
              value={form.effectiveTo}
              onValueChange={(nextValue) =>
                setForm((s) => (s ? { ...s, effectiveTo: nextValue } : s))
              }
              data-testid="field-effectiveTo"
            />
          </div>

          <button type="submit" data-testid="submit-feature" disabled={saving}>
            {t('features.save')}
          </button>
          <button
            type="button"
            onClick={() => setForm(null)}
            data-testid="cancel-edit"
          >
            {t('features.cancel')}
          </button>
        </form>
      ) : null}
    </main>
  );
}
